import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import FileDownload from "js-file-download";
import {
  Checkbox,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { PAGES } from "..";
import { uploadFile } from ".";
import { api } from "../../services/api";
import { COLORS } from "../../theme/colors";
import { copyObject } from "../../services/utils";
import { EXCEL_FILE_TYPE } from "../../constants";
import { Select } from "../../components/base/Select";
import { ContextMenu } from "../../components/contextMenu";
import trashIcon from "../../assets/images/icons/trash.svg";
import { Button, TextButton } from "../../components/base/Button";
import { UploadButton } from "../../components/base/UploadButton";
import { TransparentTextInput } from "../../components/base/TextInput";
import { saveNotification } from "../../redux/notification/actions";
import { AddressAutoComplete } from "../../components/base/AddressAutoComplete";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
  },
  lightPurple: {
    backgroundColor: "rgba(255, 230, 230, 0.2)",
  },
  table: {
    maxHeight: "60vh",
    marginTop: theme.spacing(3.75),
  },
  noTopBorder: {
    borderTop: "none",
  },
  subHeaderCell: {
    borderTop: `1px solid ${COLORS.darks[0][10]}`,
    width: "50%",
    "&>div": {
      paddingLeft: 10,
    },
  },
  title: {
    marginTop: theme.spacing(1.75),
    position: "relative",
  },
  tableActions: {
    position: "absolute",
    top: theme.spacing(2.5),
  },
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.darks[0][4],
    },
    "&:focus": {
      backgroundColor: "black",
      opacity: 0.1,
    },
  },
  trashIcon: {
    position: "absolute",
    display: "none",
    cursor: "pointer",
    width: 14,
  },
  addRow: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  saveDraft: {
    marginRight: theme.spacing(3.75),
  },
}));

const BUILDING_TYPES = [
  "Offices",
  "MultiFamilies",
  "Logistics",
  "Retail",
  "DataCenters",
  "Hotels",
];

const HEADERS = [
  { key: "name", text: "Building name/address" },
  { key: "zip_code", text: "Zip" },
  { key: "total_sqft", text: " Total sqft" },
  { key: "roof_sqft", text: "Roof sqft" },
  { key: "type", text: "Type" },
  { key: "central_smart_meter", text: "Central smart meter" },
  { key: "tenants", text: "# Tenants" },
  { key: "avg_electricity_price", text: "Avg. electricity price" },
];

const DEFAULT_ROW = {
  type: "Offices",
  central_smart_meter: false,
};

export const PortfolioSetup = () => {
  const { state } = useLocation();
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [rows, setRows] = useState(state?.data || [copyObject(DEFAULT_ROW)]);
  const rowsRef = useRef();
  rowsRef.current = rows;
  const [rightClickEvent, setRightClickEvent] = useState(null);
  const [clickedRow, setClickedRow] = useState(null);
  const [copiedRow, setCopiedRow] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadId, setUploadId] = useState(state?.upload_id || null);
  const [uploadingData, setUploadingData] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [downloadingData, setDownloadingData] = useState(false);
  const [yOffsetRowFocus, setYOffsetRowFocus] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [sortColumns, setSortColumns] = useState({});

  useEffect(() => {
    if (!state?.data) {
      api
        .get("/onboarding/draft/")
        .then((res) => res.data)
        .then((data) => {
          if (data?.data && Array.isArray(data.data)) {
            setRows(data.data);
          }
        });
    }
  }, [state?.data]);

  useEffect(() => {
    const id = setInterval(() => {
      dispatch(
        saveNotification({
          notification: "Saving...",
        })
      );

      api
        .post("/onboarding/draft/", { data: rowsRef.current })
        .then((res) => res.data)
        .then(() => {
          dispatch(
            saveNotification({
              notification: "Draft was successfully saved",
            })
          );
        })
        .catch(() => {
          dispatch(
            saveNotification({ notification: "Oops...Something went wrong" })
          );
        })
        .finally(() => {
          dispatch(saveNotification({ notification: "" }));
        });
    }, 60000);
    return () => clearInterval(id);
  }, [dispatch]);

  const canbeDone = useMemo(() => {
    if (rows.length < 1) {
      return false;
    }
    for (const row of rows) {
      if (!row.name) {
        return false;
      }
    }
    return true;
  }, [rows]);

  const onCellDataChange = (rowIndex, key, value) => {
    if (typeof value === "string") {
      value = value.trimStart();
      if (!isNaN(value)) {
        value = parseFloat(value);
      }
    }
    setRows((prev) => {
      const newRows = copyObject(prev);
      newRows[rowIndex][key] = value;
      return newRows;
    });
  };

  const uploadXls = (event) => {
    setUploading(true);
    uploadFile(dispatch, event, (data) => {
      setUploadId(data.upload_id);
      setRows(data.data);
    }).finally(() => {
      setUploading(false);
    });
  };

  const uploadData = () => {
    setUploadingData(true);
    api
      .post("/onboarding/upload-data/", { upload_id: uploadId, data: rows })
      .then((res) => res.data)
      .then(() => {
        navigate(
          `${PAGES.gettingStarted.index}/${PAGES.gettingStarted.success}`
        );
      })
      .catch(() => {
        dispatch(
          saveNotification({
            message: "Oops...Something went wrong.",
            error: true,
          })
        );
      })
      .finally(() => {
        setUploadingData(false);
      });
  };

  const downloadData = () => {
    setDownloadingData(true);
    api
      .post(
        "/onboarding/download-data-xls/",
        { data: rows },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        FileDownload(response.data, "data.xlsx");
      })
      .catch(() => {
        dispatch(
          saveNotification({
            message: "Oops...Something went wrong.",
            error: true,
          })
        );
      })
      .finally(() => {
        setDownloadingData(false);
      });
  };

  const saveDraft = () => {
    setSavingDraft(true);
    api
      .post("/onboarding/draft/", { data: rows })
      .then((res) => res.data)
      .then(() => {
        dispatch(
          saveNotification({
            message: "Draft was successfully saved.",
          })
        );
      })
      .catch(() => {
        dispatch(
          saveNotification({
            message: "Oops...Something went wrong.",
            error: true,
          })
        );
      })
      .finally(() => {
        setSavingDraft(false);
      });
  };

  const onRowHover = (e, rowIndex) => {
    setHoveredRow(rowIndex);
    const rect = e.currentTarget.getBoundingClientRect();
    setYOffsetRowFocus(rect.y + rect.height / 4);
  };

  const removeRow = () => {
    setYOffsetRowFocus(null);
    setRows(rows.slice(0, hoveredRow).concat(rows.slice(hoveredRow + 1)));
  };

  const addNewRow = () => {
    setRows((r) => {
      const newRows = copyObject(r);
      newRows.push(copyObject(DEFAULT_ROW));
      return newRows;
    });
  };

  const addRowAbove = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow && copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const addRowBelow = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex + 1);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex + 1)));
    if (copiedRow && copiedRow > rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const deleteRow = (rowIndex) => {
    if (rows.length === 1) return;
    setRows(rows.slice(0, rowIndex).concat(rows.slice(rowIndex + 1)));
    if (copiedRow > rowIndex) {
      setCopiedRow(copiedRow - 1);
    } else if (copiedRow === rowIndex) {
      setCopiedRow(null);
    }
  };

  const copyRow = (rowIndex) => {
    setCopiedRow(rowIndex);
  };

  const pasteRow = (rowIndex) => {
    if (copiedRow === null) return;
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(rows[copiedRow]);
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const sortByColumn = (name) => {
    const columns = {};
    if (!sortColumns[name]) {
      columns[name] = "asc";
    } else if (sortColumns[name] === "asc") {
      columns[name] = "desc";
    } else {
      columns[name] = "asc";
    }
    setSortColumns(columns);
    setRows((r) => {
      const emptyRows = [];
      const rows = [];
      r.forEach((obj) => {
        if (!!obj[name]) {
          rows.push(obj);
        } else {
          emptyRows.push(obj);
        }
      });
      rows.sort((a, b) => {
        if (a[name] > b[name]) {
          return 1;
        } else if (a[name] < b[name]) {
          return -1;
        }
        return 0;
      });
      if (columns[name] === "asc") return rows.concat(emptyRows);
      return rows.reverse().concat(emptyRows);
    });
  };

  const contextMenuItems = [
    { text: "Add row above", onClick: addRowAbove },
    { text: "Add row below", onClick: addRowBelow },
    { text: "Delete row", onClick: deleteRow },
    { text: "Copy", onClick: copyRow },
    { text: "Paste", onClick: pasteRow },
  ];

  return (
    <div className={classes.root}>
      <Tooltip title="Remove row" placement="top">
        <img
          onClick={removeRow}
          className={classes.trashIcon}
          style={{
            top: yOffsetRowFocus,
            left: 12,
            display: yOffsetRowFocus ? "block" : "none",
          }}
          src={trashIcon}
          alt="trash-icon"
        />
      </Tooltip>
      <div className={classes.title}>
        <Typography style={{ fontWeight: 400 }} variant="h2">
          Portfolio Setup (
          {rows.length > 1
            ? `${rows.length} buildings`
            : `${rows.length} building`}
          )
        </Typography>

        <Grid
          className={classes.tableActions}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <div className={classes.saveDraft}>
            <TextButton
              text="DOWNLOAD DATA"
              onClick={downloadData}
              loading={downloadingData}
              disabled={!canbeDone}
            />
          </div>
          <div className={classes.saveDraft}>
            <TextButton
              text="SAVE DRAFT"
              onClick={saveDraft}
              loading={savingDraft}
            />
          </div>
          <UploadButton
            accept={EXCEL_FILE_TYPE}
            loading={uploading}
            onChange={uploadXls}
            text="UPLOAD FILE"
            onlyText
          />
        </Grid>
      </div>

      <TableContainer className={classes.table}>
        <Table aria-label="simple table" style={{ height: "100%" }}>
          <TableHead>
            <TableRow>
              {HEADERS.map((header, index) => (
                <TableCell key={index}>
                  <TableSortLabel
                    active={!!sortColumns[header.key]}
                    direction={sortColumns[header.key] || "asc"}
                    onClick={() => sortByColumn(header.key)}
                  >
                    {header.text}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell
                className={classes.headerCell}
                align="left"
                colSpan={2}
                style={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                }}
              >
                <Grid
                  container
                  direction="column"
                  style={{ height: "100%", flexWrap: "nowrap" }}
                >
                  <Grid
                    container
                    style={{
                      paddingLeft: 10,
                      height: "100%",
                    }}
                    alignItems="center"
                  >
                    Est. electricity consumption
                  </Grid>
                  <Grid
                    container
                    style={{ height: "100%", flexWrap: "nowrap" }}
                  >
                    <TableSortLabel
                      className={classes.subHeaderCell}
                      active={!!sortColumns.consumption_monthly}
                      direction={sortColumns.consumption_monthly || "asc"}
                      onClick={() => sortByColumn("consumption_monthly")}
                    >
                      <div>Monthly (kWh)</div>
                    </TableSortLabel>
                    <TableSortLabel
                      className={classes.subHeaderCell}
                      active={!!sortColumns.consumption_yearly}
                      direction={sortColumns.consumption_yearly || "asc"}
                      onClick={() => sortByColumn("consumption_yearly")}
                      style={{ borderLeft: `1px solid ${COLORS.darks[0][10]}` }}
                    >
                      <div>Yearly (kWh)</div>
                    </TableSortLabel>
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                onMouseEnter={(e) => onRowHover(e, index)}
                className={classes.row}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setRightClickEvent(event);
                  setClickedRow(index);
                }}
                key={index}
              >
                <TableCell className={row.name ? null : classes.lightPurple}>
                  <AddressAutoComplete
                    value={row.name}
                    onSeleted={(address) => {
                      onCellDataChange(
                        index,
                        "name",
                        address.formatted_address
                      );
                    }}
                    onChange={(v) => onCellDataChange(index, "name", v)}
                  />
                </TableCell>
                <TableCell
                  width={60}
                  className={row.zip_code ? null : classes.lightPurple}
                >
                  <TransparentTextInput
                    value={row.zip_code}
                    onChange={(v) =>
                      onCellDataChange(index, "zip_code", v.toUpperCase())
                    }
                  />
                </TableCell>

                <TableCell
                  width={60}
                  className={row.total_sqft ? null : classes.lightPurple}
                >
                  <TransparentTextInput
                    type="number"
                    value={row.total_sqft}
                    onChange={(v) => onCellDataChange(index, "total_sqft", v)}
                  />
                </TableCell>
                <TableCell
                  width={60}
                  className={row.roof_sqft ? null : classes.lightPurple}
                >
                  <TransparentTextInput
                    type="number"
                    value={row.roof_sqft}
                    onChange={(v) => onCellDataChange(index, "roof_sqft", v)}
                  />
                </TableCell>
                <TableCell width={1}>
                  <Select
                    options={BUILDING_TYPES}
                    value={row.type}
                    onChange={(v) => onCellDataChange(index, "type", v)}
                  />
                </TableCell>
                <TableCell width={1} align="center">
                  <Checkbox
                    color="primary"
                    checked={
                      !!row.central_smart_meter &&
                      row.central_smart_meter.toString().toLowerCase() !==
                        "no" &&
                      row.central_smart_meter.toString().toLowerCase() !==
                        "false"
                    }
                    onChange={(e) =>
                      onCellDataChange(
                        index,
                        "central_smart_meter",
                        e.target.checked
                      )
                    }
                    inputProps={{ "aria-label": "primary checkbox" }}
                  />
                </TableCell>
                <TableCell
                  width={1}
                  className={row.tenants ? null : classes.lightPurple}
                >
                  <TransparentTextInput
                    type="number"
                    value={row.tenants}
                    onChange={(v) => onCellDataChange(index, "tenants", v)}
                  />
                </TableCell>
                <TableCell
                  width={100}
                  className={
                    row.avg_electricity_price ? null : classes.lightPurple
                  }
                >
                  <TransparentTextInput
                    type="number"
                    value={row.avg_electricity_price}
                    onChange={(v) =>
                      onCellDataChange(index, "avg_electricity_price", v)
                    }
                  />
                </TableCell>
                <TableCell
                  width={100}
                  className={
                    row.monthly_kwh_consumption ? null : classes.lightPurple
                  }
                >
                  <TransparentTextInput
                    type="number"
                    value={row.monthly_kwh_consumption}
                    onChange={(v) =>
                      onCellDataChange(index, "monthly_kwh_consumption", v)
                    }
                  />
                </TableCell>
                <TableCell
                  width={100}
                  className={
                    row.yearly_kwh_consumption ? null : classes.lightPurple
                  }
                >
                  <TransparentTextInput
                    type="number"
                    value={row.yearly_kwh_consumption}
                    onChange={(v) =>
                      onCellDataChange(index, "yearly_kwh_consumption", v)
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ContextMenu
        items={contextMenuItems}
        event={rightClickEvent}
        onClose={() => {
          setRightClickEvent(null);
          setClickedRow(null);
        }}
        clickedRow={clickedRow}
        copiedRow={copiedRow}
      />

      <div className={classes.addRow}>
        <TextButton text="+ Add row" onClick={addNewRow} />
      </div>

      <Grid container justifyContent="center">
        <Button
          text="I’M DONE"
          next
          onClick={uploadData}
          loading={uploadingData}
          disabled={!canbeDone}
        />
      </Grid>
    </div>
  );
};
